// 🔧 qr_scanner_modal.js
// ✅ Purpose: Reusable LGV QR scanner modal
// ✅ Features:
// - Opens device camera inside the app
// - Scans QR codes using html5-qrcode
// - Returns decoded QR text to the calling page
// - Does not contain Passport, Voting, Ticket, Vendor, Sponsor, or Auction logic
// - Reusable across LGV modules
// - Safely stops camera when closed or after successful scan
// - Shows success/error messages inside the modal
// - Fully block-commented for clarity and future devs

import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

import { logger } from "../../utils/logger";

const QR_READER_ELEMENT_ID = "lgv-qr-scanner-reader";

const QrScannerModal = ({
  show,
  title = "Scan QR Code",
  helpText = "Align the QR code inside the scanner.",
  onClose,
  onScan,
}) => {
  const scannerRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [scanning, setScanning] = useState(false);

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scanning) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (err) {
      logger.warn("⚠ QR scanner stop warning:", {
        value: err,
      });
    } finally {
      scannerRef.current = null;
      setScanning(false);
    }
  };

  const startScanner = async () => {
    try {
      setStarting(true);
      setStatus(null);

      const scanner = new Html5Qrcode(QR_READER_ELEMENT_ID);
      scannerRef.current = scanner;

      await scanner.start(
        {
          facingMode: "environment",
        },
        {
          fps: 10,
          qrbox: {
            width: 250,
            height: 250,
          },
        },
        async (decodedText) => {
          setStatus({
            type: "success",
            message: "QR code scanned.",
          });

          await stopScanner();

          if (typeof onScan === "function") {
            onScan(decodedText);
          }
        },
        () => {
          // Scan misses happen constantly while camera is running.
          // We intentionally ignore per-frame scan errors.
        }
      );

      setScanning(true);
    } catch (err) {
      const cameraPermissionDismissed =
        err?.name === "NotAllowedError" ||
        String(err?.message || "").includes("NotAllowedError");
      const log = cameraPermissionDismissed ? logger.warn : logger.error;

      log("❌ Failed to start QR scanner:", {
        value: err,
      });

      setStatus({
        type: "error",
        message:
          "Unable to start the camera. Please check camera permissions or enter the code manually.",
      });
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    startScanner();

    return () => {
      stopScanner();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleClose = async () => {
    await stopScanner();

    if (typeof onClose === "function") {
      onClose();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{
        backgroundColor: "rgba(0,0,0,0.65)",
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title">
              📷 {title}
            </h5>

            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleClose}
            />
          </div>

          <div className="modal-body">
            {status?.message && (
              <div
                className={`alert ${
                  status.type === "success"
                    ? "alert-success"
                    : "alert-danger"
                }`}
              >
                {status.message}
              </div>
            )}

            <p className="text-muted">
              {helpText}
            </p>

            <div
              id={QR_READER_ELEMENT_ID}
              className="border rounded bg-light"
              style={{
                width: "100%",
                minHeight: "320px",
                overflow: "hidden",
              }}
            />

            {starting && (
              <div className="text-muted mt-3">
                Starting camera...
              </div>
            )}

            <div className="alert alert-light border mt-3 mb-0">
              If scanning does not work, close this window and enter the code manually.
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrScannerModal;
